<template>
  <BaseModal
    class="onboarding-modal app-container"
    :class="{ 'first-step': isFirstStep }"
    :full-screen="isMobile"
    flush-content
    ui-refresh>
    <template #body>
      <BaseCarousel
        ref="carousel"
        :start-index="currentStepIndex"
        :items="steps"
        :items-per-slide="1"
        :indicator-container
        :wrap="false"
        :allow-indicator-click="false"
        auto-scroll-behavior="disabled"
        class="mx-auto">
        <template #default="{ item }">
          <div :class="['layout-container', { 'first-step': item.isFirstStep }]">
            <div
              v-if="item.isFirstStep"
              class="header-image"
              :style="{ backgroundImage: `url('${item.imageUrl}')` }"/>
            <OnboardingStep
              :title="item.title"
              :text="item.text"
              :image-url="item.imageUrl"
              :image-class="item.imageClass"
              :is-first-step="item.isFirstStep">
              <component
                :is="item.component"
                v-if="item.component"/>
              <OnboardingButtons
                v-if="!isMobile && item.isFirstStep"
                :button-text="item.buttonText"
                :show-back-button="item.showBackButton"
                :is-first-step="item.isFirstStep"
                @next-step="nextStep"
                @previous-step="previousStep"
                @close="closeOnboardingModal">
                <template
                  v-if="item.actionButton"
                  #actionButton>
                  <BaseButton
                    :text="item.actionButton.text"
                    :variant="item.actionButton.variant"
                    class="w-100"
                    @click="item.actionButton.onClick"/>
                </template>
              </OnboardingButtons>
            </OnboardingStep>
          </div>
        </template>
      </BaseCarousel>
    </template>
    <template #footer>
      <div
        v-show="showIndicators"
        ref="indicatorContainer"
        class="pb-md"/>
      <OnboardingButtons
        v-if="isMobile || !activeItem.isFirstStep"
        :button-text="activeItem.buttonText"
        :show-back-button="activeItem.showBackButton"
        :is-first-step="activeItem.isFirstStep"
        @next-step="nextStep"
        @previous-step="previousStep"
        @close="closeOnboardingModal">
        <template
          v-if="activeItem.actionButton"
          #actionButton>
          <BaseButton
            :text="activeItem.actionButton.text"
            :variant="activeItem.actionButton.variant"
            class="w-100"
            :to="activeItem.actionButton.to"
            @click="activeItem.actionButton.onClick"/>
        </template>
      </OnboardingButtons>
    </template>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/global/BaseModal.vue'
import BaseCarousel from '@shared/components/ADORN/BaseCarousel.vue'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import useScreenSize from '@shared/composables/screenSize.js'
import useModals from '@/composables/modals.js'
import { computed, nextTick, onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue'
import { useHideIntercomLauncher } from '@shared/composables/intercom.js'
import OnboardingButtons from '@/components/onboarding/OnboardingButtons.vue'
import OnboardingStep from '@/components/onboarding/OnboardingStep.vue'
import { storeToRefs } from 'pinia'
import { useOnboardingStore } from '@/stores/onboarding.js'
import { useRouter } from 'vue-router'
import useAnalytics from '@shared/composables/analytics.js'

useHideIntercomLauncher()
const { isMobile } = useScreenSize()
const { closeOnboardingModal } = useModals()

const carousel = useTemplateRef('carousel')
const indicatorContainer = useTemplateRef('indicatorContainer')

const onboardingStore = useOnboardingStore()
const { currentStepIndex, steps } = storeToRefs(onboardingStore)
const { onboardingStepViewed } = onboardingStore

const { trackOnboardingInteraction } = useAnalytics()

const activeIndex = computed(() => carousel.value?.currentIndex ?? 0)
const activeItem = computed(() => steps.value[activeIndex.value])
const isFirstStep = computed(() => activeIndex.value === 0)
const isLastStep = computed(() => activeIndex.value === steps.value.length - 1)
const showIndicators = computed(() => !isFirstStep.value)

watch(activeIndex, onboardingStepViewed)
onMounted(() => {
  onboardingStepViewed(activeIndex.value)
  trackOnboardingInteraction('open')
})
watch(currentStepIndex, (index, oldIndex) => {
  trackOnboardingInteraction('click', {
    button: index > oldIndex ? 'next' : 'prev'
  })
})
onBeforeUnmount(() => {
  trackOnboardingInteraction('dismiss')
})

const router = useRouter()
function nextStep () {
  if (isLastStep.value) {
    onboardingStepViewed(activeIndex.value + 1)
    closeOnboardingModal()
    nextTick(() => {
      router.push({ name: 'closet-sections' })
    })
  } else {
    carousel.value.next()
  }
}

function previousStep () {
  carousel.value.prev()
}
</script>

<style lang="scss" scoped>
.onboarding-modal {
  // Modal Backdrop Styles
  :deep(.modal-backdrop) {
    --bs-backdrop-opacity: 0.75;
    --bs-backdrop-bg: #{$armor};
  }

  // Base Modal Content Styles
  :deep(.modal-content) {
    background-color: $origami;
    display: grid;
    grid-template-rows: auto 1fr auto;
  }

  // Make every parent element of the image full height
  @include media-mobile {
    :deep(.scrollable) {
      &, .container-fluid {
        display: flex;
      }

      .carousel-wrapper,
      .carousel-slide {
        height: 100% !important;
      }
    }
  }
  &:not(.first-step) {
    :deep(.carousel-slide) {
      &, & > * {
        height: 100% !important;
      }
    }
  }

  // Prevent drop shadows from being cut off.
  :deep(.scrollable) {
    &, section {
      overflow: visible;
    }
  }

  // Modal Header Styles
  :deep(.modal-header) {
    @include transition-base(all, $transition-duration: 1.2s);
    padding: $spacing-md;

    .close-button {
      color: $armor;
      width: 32px;
      height: 32px;
      padding: 4px;
      top: 11px;
      right: 11px;
      position: unset;

      svg {
        width: 24px;
        height: 24px;
      }
    }
  }
  @include media-tablet-or-larger {
    &.first-step {
      :deep(.modal-header) .close-button {
        position: absolute;
      }
    }
  }

  // Modal Footer Styles
  :deep(.footer) {
    @include transition-base(all, $transition-duration: 1.2s);
    background: $origami;
    padding: $spacing-md !important;
    border: none;
    position: unset;
  }

  // Layout Container Styles
  .layout-container {
    display: grid;
    height: 100%;
  }

  // Carousel Styles
  :deep(.carousel-wrapper) {
    @include media-tablet-or-larger {
      min-height: 480px; // Prevent carousel auto-sizing from collapsing the slides.
    }

    .row {
      --bs-gutter-x: 0;
    }
  }
  :deep(.carousel-indicator-container) {
    column-gap: $spacing-xs;

    button {
      // Hide first indicator
      &:first-child {
        display: none;
      }

      --indicator-size: 8px;
      padding: 0;
      border: none;
      background-color: $ash;
      opacity: 1;

      &.active {
        background-color: $orchid;
      }
    }
  }

  // Button Styles
  :deep(.base-button.stylist) {
    width: unset;
  }

  // First Step Specific Styles
  &.first-step {
    @include media-tablet-or-larger {
      :deep(.modal-header),
      :deep(.footer) {
        padding: 0 !important;
      }
    }
  }
  .layout-container.first-step {
      grid-template-rows: 1fr auto;

      .header-image {
        background-size: cover;
        background-position: 38% top;
        background-repeat: no-repeat;

        @include media-mobile {
          height: 100%;
          background-position-y: -108px;
          background-size: 130%;
        }
      }

      @include media-mobile {
        gap: $spacing-md;
      }

      @include media-tablet-or-larger {
        grid-template-columns: auto 1fr;
        align-items: center;

        .header-image {
          height: 100%;
          width: 192px;
        }
      }
    }
}
</style>
